/**
 * Contexto de la cuenta de WhatsApp oficial de la marca: se carga al entrar a Activa (lo pone `DashboardLayout`, ver
 * `CuentaOficialProvider`) y otra vez cada vez que se entra a la sección de WhatsApp (ver `ProveedorCuenta`).
 *
 * Una sola fuente: `GET /cuenta` siempre devuelve una CuentaPublica (la virtual, con `id: null`,
 * si la marca todavía no tiene cuenta). De ahí salen el modo del transporte y la config. No hay
 * defaults en el front: si el pedido falla, `config` queda null y las pantallas que la necesitan
 * muestran el error en vez de inventar un ritmo, una franja o un texto de baja.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import type { ConfigCuenta, CuentaPublica, ModoTransporte } from "./types";
import { oficialApi } from "./api";
import { leerError, leerRespuesta, textoError } from "./logic/respuesta";

export interface CuentaOficialCtx {
  cargando: boolean; error: string;
  modo: ModoTransporte | null;          // cuenta.modoTransporte; null mientras carga o si falló
  cuenta: CuentaPublica | null;         // null SÓLO mientras carga o si el pedido falló; sin cuenta real llega la virtual (id:null)
  config: ConfigCuenta | null;          // cuenta?.config ?? null
  tieneCuenta: boolean;                 // cuenta?.id != null
  recargar: () => Promise<void>;
}

const Ctx = createContext<CuentaOficialCtx | null>(null);

/**
 * Desde que las secciones de WhatsApp viven en el menú lateral, el proveedor va en `DashboardLayout`: el menú
 * necesita la cuenta para el punto de «Configuración», y el menú está fuera de `OficialLayout`. `OficialLayout`
 * lo sigue poniendo (sus tests lo montan solo): si ya hay uno arriba, no arma otro. Dos proveedores serían dos
 * cuentas, y «Guardar» en Configuración recargaría la de las páginas y dejaría viejo el punto del menú.
 */
export function CuentaOficialProvider({ children }: { children: ReactNode }): JSX.Element {
  const yaHay = useContext(Ctx);
  if (yaHay) return <>{children}</>;
  return <ProveedorCuenta>{children}</ProveedorCuenta>;
}

/** La sección de WhatsApp oficial (`OficialLayout`). No sale de `SeccionesOficial`: ése importa este archivo. */
const R_SECCION = "/activa/oficial";
const enLaSeccion = (pathname: string) => pathname === R_SECCION || pathname.startsWith(`${R_SECCION}/`);

/**
 * Pide la cuenta al montar y otra vez cada vez que se ENTRA a la sección de WhatsApp desde afuera (Reactivación,
 * Accesos…). Moverse entre páginas de la sección no la vuelve a pedir.
 *
 * Mientras el proveedor vivía en `OficialLayout`, que se monta en cada entrada a la sección, entrar era pedirla de
 * nuevo. Al subirlo a `DashboardLayout` (por el punto del menú) quedó un solo pedido por sesión: si ése fallaba (un
 * corte de red), «No pudimos traer la configuración» quedaba en todas las pantallas de WhatsApp hasta recargar el
 * navegador, y una marca frenada o conectada desde otro lado no se veía, ni en el punto del menú.
 *
 * La entrada se detecta DURANTE el render (guardando el valor del render anterior, como sugiere React) y no en un
 * efecto, para que la página a la que se entra ya se dibuje con `cargando: true`: los efectos de los hijos corren
 * antes que los del padre, y con un efecto acá o en `OficialLayout` la página montaba con la cuenta vieja y
 * `cargando: false` (Configuración elegía su pestaña por defecto con una marca que ya estaba frenada). El pedido sale
 * en el efecto, disparado por `entradas`. Al montar no hay entrada (`adentroAntes` arranca igual), así que la primera
 * carga hace un solo pedido aunque se abra adentro de la sección. Cambiar de comercio no pasa por acá: `SelectorMarca`
 * recarga la página entera, y el proveedor vuelve a montarse con el comercio nuevo.
 */
function ProveedorCuenta({ children }: { children: ReactNode }): JSX.Element {
  const [cuenta, setCuenta] = useState<CuentaPublica | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const adentro = enLaSeccion(useLocation().pathname);
  const [adentroAntes, setAdentroAntes] = useState(adentro);
  const [entradas, setEntradas] = useState(0);
  if (adentro !== adentroAntes) {
    setAdentroAntes(adentro);
    if (adentro) {
      setEntradas((n) => n + 1);
      // La cuenta que ya había se queda a la vista mientras llega la nueva; el error de un pedido anterior no.
      setCargando(true);
      setError("");
    }
  }
  // Descarta una respuesta vieja si llegó después de una recarga más nueva (dos «Guardar» seguidos).
  const seq = useRef(0);
  const montado = useRef(true);

  const recargar = useCallback(async () => {
    const mio = ++seq.current;
    setCargando(true);
    try {
      const l = leerRespuesta<CuentaPublica>(await oficialApi.cuenta());
      if (!montado.current || mio !== seq.current) return;
      if (!l.ok) {
        setCuenta(null);
        setError(textoError(l));
      } else {
        setCuenta(l.data ?? null);
        setError(l.data ? "" : "El servidor no devolvió la cuenta.");
      }
    } catch (e) {
      if (!montado.current || mio !== seq.current) return;
      setCuenta(null);
      setError(textoError(leerError(e)));
    } finally {
      if (montado.current && mio === seq.current) setCargando(false);
    }
  }, []);

  useEffect(() => {
    montado.current = true;
    return () => { montado.current = false; };
  }, []);

  // Al montar, y en cada entrada a la sección.
  useEffect(() => { void recargar(); }, [recargar, entradas]);

  const valor = useMemo<CuentaOficialCtx>(() => ({
    cargando,
    error,
    modo: cuenta?.modoTransporte ?? null,
    cuenta,
    config: cuenta?.config ?? null,
    tieneCuenta: cuenta?.id != null,
    recargar,
  }), [cargando, error, cuenta, recargar]);

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

/** La cuenta de la marca. Fuera de `CuentaOficialProvider` es un error de programación: se avisa en vez de devolver defaults. */
export function useCuentaOficial(): CuentaOficialCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useCuentaOficial se usó fuera de CuentaOficialProvider (va dentro de OficialLayout).");
  return v;
}

/** Para el menú lateral: la cuenta si hay un proveedor arriba, `null` si no (sin proveedor el menú no avisa nada). */
export function useCuentaOficialOpcional(): CuentaOficialCtx | null {
  return useContext(Ctx);
}
